import type { ColumnOrderState, VisibilityState } from "@tanstack/react-table";
import { useEffect } from "react";

type ColumnStateParser<T> = {
	parse: (value: string) => T | null;
	serialize: (value: T) => string;
};

type PersistedColumnPreferences = {
	version: 1;
	visibility: string;
	order: string;
};

type ParsedColumnPreferences = {
	visibility?: VisibilityState;
	order?: ColumnOrderState;
};

interface ReadColumnPreferenceDefaultsOptions {
	storageKey: string;
	visibilityParam: string;
	orderParam: string;
	defaultVisibility: VisibilityState;
	defaultOrder: ColumnOrderState;
	visibilityParser: ColumnStateParser<VisibilityState>;
	orderParser: ColumnStateParser<ColumnOrderState>;
}

interface UsePersistedColumnPreferencesOptions {
	storageKey: string;
	visibility: VisibilityState;
	order: ColumnOrderState;
	visibilityParser: ColumnStateParser<VisibilityState>;
	orderParser: ColumnStateParser<ColumnOrderState>;
}

function readStoredColumnPreferences(
	storageKey: string,
	visibilityParser: ColumnStateParser<VisibilityState>,
	orderParser: ColumnStateParser<ColumnOrderState>,
): ParsedColumnPreferences | null {
	try {
		const raw = localStorage.getItem(storageKey);
		if (raw === null) return null;

		const value = JSON.parse(raw) as unknown;
		if (!value || typeof value !== "object") return null;

		const record = value as Partial<
			Record<keyof PersistedColumnPreferences, unknown>
		>;
		const visibility =
			typeof record.visibility === "string"
				? (visibilityParser.parse(record.visibility) ?? undefined)
				: undefined;
		const order =
			typeof record.order === "string"
				? (orderParser.parse(record.order) ?? undefined)
				: undefined;

		if (!visibility && !order) return null;
		return { visibility, order };
	} catch {
		return null;
	}
}

export function readColumnPreferenceDefaults({
	storageKey,
	visibilityParam,
	orderParam,
	defaultVisibility,
	defaultOrder,
	visibilityParser,
	orderParser,
}: ReadColumnPreferenceDefaultsOptions): {
	visibility: VisibilityState;
	order: ColumnOrderState;
} {
	const params =
		typeof window === "undefined"
			? null
			: new URLSearchParams(window.location.search);
	const saved = readStoredColumnPreferences(
		storageKey,
		visibilityParser,
		orderParser,
	);

	return {
		visibility:
			params?.has(visibilityParam) || !saved?.visibility
				? defaultVisibility
				: saved.visibility,
		order:
			params?.has(orderParam) || !saved?.order ? defaultOrder : saved.order,
	};
}

export function usePersistedColumnPreferences({
	storageKey,
	visibility,
	order,
	visibilityParser,
	orderParser,
}: UsePersistedColumnPreferencesOptions): void {
	useEffect(() => {
		try {
			const value: PersistedColumnPreferences = {
				version: 1,
				visibility: visibilityParser.serialize(visibility),
				order: orderParser.serialize(order),
			};
			localStorage.setItem(storageKey, JSON.stringify(value));
		} catch {
			// Ignore storage failures (private browsing, quota, disabled storage).
		}
	}, [storageKey, visibility, order, visibilityParser, orderParser]);
}
